const LEGACY_RUN_KEY = "__legacy__";
const SQUARE_RATIO_TOLERANCE = 0.05;

const ORIENTATION_ORDER = {
  square: 0,
  vertical: 1,
  horizontal: 2,
  unknown: 3,
};

const formatIDFromArtboard = (artboardID) => {
  const raw = String(artboardID || "").trim();
  const index = raw.indexOf("__");
  return index > 0 ? raw.slice(0, index) : raw;
};

const hasDimensions = ({ width, height }) =>
  Number(width) > 0 && Number(height) > 0;

export const getProduceOrientation = ({ width, height } = {}) => {
  if (!hasDimensions({ width, height })) return "unknown";
  const ratio = Number(width) / Number(height);
  if (Math.abs(ratio - 1) <= SQUARE_RATIO_TOLERANCE) return "square";
  return ratio < 1 ? "vertical" : "horizontal";
};

const getSectionKey = ({ item }) => {
  const formatID = formatIDFromArtboard(item?.artboardID);
  if (formatID) return formatID;
  if (hasDimensions(item || {})) return `${item.width}x${item.height}`;
  return "other";
};

const getRunTimestamp = ({ jobID, runs = {}, items = [] }) => {
  const fromRun = Number(runs?.[jobID]?.createdAt);
  if (Number.isFinite(fromRun) && fromRun > 0) return fromRun;
  const times = items
    .map((item) => Number(item.creationTime))
    .filter((value) => Number.isFinite(value) && value > 0);
  return times.length > 0 ? Math.min(...times) : 0;
};

const buildSections = ({ items }) => {
  const sections = new Map();
  items.forEach((item) => {
    const key = getSectionKey({ item });
    if (!sections.has(key)) sections.set(key, { key, items: [] });
    sections.get(key).items.push(item);
  });

  return [...sections.values()]
    .map((section) => {
      const reference =
        section.items.find((item) => hasDimensions(item)) || section.items[0];
      const width = Number(reference?.width) || 0;
      const height = Number(reference?.height) || 0;
      return {
        ...section,
        width,
        height,
        orientation: getProduceOrientation({ width, height }),
      };
    })
    .sort(
      (left, right) =>
        ORIENTATION_ORDER[left.orientation] -
          ORIENTATION_ORDER[right.orientation] ||
        right.width * right.height - left.width * left.height ||
        left.key.localeCompare(right.key)
    );
};

/**
 * Groups gallery items by generation run (jobID) and, inside each run,
 * by size/format: square first, then vertical, then horizontal.
 * Items without a jobID (older produces) collapse into one legacy group.
 */
export const groupProduceItemsByRun = ({ items = [], runs = {} } = {}) => {
  const byRun = new Map();
  items.forEach((item) => {
    const key = item?.jobID || LEGACY_RUN_KEY;
    if (!byRun.has(key)) byRun.set(key, []);
    byRun.get(key).push(item);
  });

  return [...byRun.entries()]
    .map(([key, runItems]) => ({
      key,
      isLegacy: key === LEGACY_RUN_KEY,
      timestamp:
        key === LEGACY_RUN_KEY
          ? 0
          : getRunTimestamp({ jobID: key, runs, items: runItems }),
      sections: buildSections({ items: runItems }),
    }))
    .sort((left, right) => {
      if (left.isLegacy !== right.isLegacy) return left.isLegacy ? 1 : -1;
      return right.timestamp - left.timestamp;
    });
};
